import { FiltersCombobox } from '@/components/custom/filters-combobox'
import { ResetFiltersMenu } from '@/components/custom/reset-filters-menu'
import { SaveViewDropdown } from '@/components/custom/save-view-dropdown'
import { SortCombobox } from '@/components/custom/sort-combobox'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { FilterOption } from '@/lib/utils'
import type { SortingState } from '@tanstack/react-table'
import { useCallback, useMemo, memo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAutoOpenPopover } from '@/hooks/use-auto-open-popover'
import {
  FilterValueEditor,
  getFilterEditorType,
  getDefaultOperator,
  formatFilterDisplayValue,
  getOperatorLabel,
} from '@/components/custom/filters'
import type { FilterOperator } from '@/types/api'

export interface ActiveFilter {
  label: string
  filterKey: string
  filterType?: string
  dataType?: string
  listContent?: string[]
}

interface FilterChipProps {
  filter: ActiveFilter
  value: unknown
  operator: FilterOperator
  onValueChange: (filterKey: string, value: unknown) => void
  onOperatorChange: (filterKey: string, operator: FilterOperator) => void
  onRemove: (filterKey: string) => void
  isNewlyAdded?: boolean
  onClearNewlyAdded?: () => void
}

function FilterChip({
  filter,
  value,
  operator,
  onValueChange,
  onOperatorChange,
  onRemove,
  isNewlyAdded = false,
  onClearNewlyAdded,
}: FilterChipProps) {
  const [open, setOpen] = useState(false)

  useAutoOpenPopover(isNewlyAdded, setOpen, onClearNewlyAdded)

  const hasOptions = filter.listContent && filter.listContent.length > 0
  const editorType = getFilterEditorType(filter.dataType, hasOptions ?? false)

  const handleValueChange = useCallback(
    (newValue: unknown) => {
      onValueChange(filter.filterKey, newValue)
    },
    [filter.filterKey, onValueChange]
  )

  const handleOperatorChange = useCallback(
    (newOperator: FilterOperator) => {
      onOperatorChange(filter.filterKey, newOperator)
    },
    [filter.filterKey, onOperatorChange]
  )

  const handleRemove = useCallback(() => {
    onRemove(filter.filterKey)
    setOpen(false)
  }, [filter.filterKey, onRemove])

  const displayText = useMemo(() => {
    const formattedValue = formatFilterDisplayValue(value, operator, editorType)
    if (!formattedValue) return ''

    if (editorType === 'enum') {
      return `: ${formattedValue}`
    }

    const operatorLabel = getOperatorLabel(operator, editorType)
    if (editorType === 'boolean') {
      return `: ${formattedValue}`
    }

    return ` ${operatorLabel} ${formattedValue}`
  }, [value, operator, editorType])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-8 w-full max-w-min gap-2 border-primary/50 bg-primary/8 font-univers text-xs text-primary shadow-none [&>svg]:text-primary hover:bg-primary/12',
            'data-[state=open]:border-primary/50 data-[state=open]:bg-primary/12 data-[state=open]:text-primary data-[state=open]:[&>svg]:text-primary'
          )}
        >
          <span className="truncate min-w-0 flex-1 text-left">
            {filter.label}
            {displayText}
          </span>
          <ChevronDown className="flex-shrink-0 h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 border-primary/50 shadow-[0px_10px_21px_0px_#0000001A,0px_39px_39px_0px_#00000017,0px_87px_52px_0px_#0000000D,0px_154px_62px_0px_#00000003,0px_241px_67px_0px_#00000000]"
        align="start"
        sideOffset={8}
      >
        <FilterValueEditor
          editorType={editorType}
          value={value}
          operator={operator}
          options={filter.listContent}
          onValueChange={handleValueChange}
          onOperatorChange={handleOperatorChange}
          onRemove={handleRemove}
          label={filter.label}
          fieldName={filter.filterKey}
        />
      </PopoverContent>
    </Popover>
  )
}

interface FiltersMenuProps {
  activeFilters: ActiveFilter[]
  filtersList?: FilterOption[]
  filterValues?: Record<string, unknown>
  filterOperators?: Record<string, FilterOperator>
  sorting?: SortingState
  sortsList?: FilterOption[]
  newlyAddedFilter?: string | null
  newlyAddedSort?: string | null
  isLoading?: boolean
  totalMatches?: number
  clearAllFilters: () => void
  clearSorts?: (sortId?: string) => void
  onFilterValueChange?: (filterKey: string, value: unknown) => void
  onFilterOperatorChange?: (filterKey: string, operator: FilterOperator) => void
  onFilterRemove?: (filterKey: string) => void
  onFilterSelect?: (filterKey: string, filtersGroup: number) => void
  onClearNewlyAddedFilter?: () => void
  onSortSelect?: (sortValue: string) => void
  onSortUpdate?: (oldSort: string, newSortValue: SortingState) => void
  onSortReorder?: (newSorting: SortingState) => void
  onClearNewlyAddedSort?: () => void
  /** Whether there are unsaved filter/sort changes */
  hasUnsavedChanges?: boolean
  /** Callback to save filter/sort changes to the view */
  onSaveView?: () => void
  /** Whether the user can update the current view */
  canUpdateView?: boolean
  /** Whether save is in progress */
  isSaving?: boolean
  /** Callback to revert to saved view state */
  onRevertToSaved?: () => void
  /** Callback to open Create View modal (save as new) */
  onSaveAsNewView?: () => void
}

function FiltersMenu({
  activeFilters,
  filtersList = [],
  filterValues = {},
  filterOperators = {},
  sorting = [],
  sortsList = [],
  newlyAddedFilter,
  newlyAddedSort,
  isLoading = false,
  totalMatches,
  clearAllFilters,
  clearSorts,
  onFilterValueChange,
  onFilterOperatorChange,
  onFilterRemove,
  onFilterSelect,
  onClearNewlyAddedFilter,
  onSortSelect,
  onSortUpdate,
  onSortReorder,
  onClearNewlyAddedSort,
  hasUnsavedChanges = false,
  onSaveView,
  canUpdateView = false,
  isSaving = false,
  onRevertToSaved,
  onSaveAsNewView,
}: FiltersMenuProps) {
  const hasFilters = useMemo(
    () => activeFilters && activeFilters.length > 0,
    [activeFilters]
  )

  const hasSorts = useMemo(() => sorting && sorting.length > 0, [sorting])

  const handleClearAll = useCallback(() => {
    if (hasSorts && clearSorts) {
      clearSorts()
    }
    if (hasFilters) {
      clearAllFilters()
    }
  }, [hasSorts, hasFilters, clearSorts, clearAllFilters])

  const handleValueChange = useCallback(
    (filterKey: string, value: unknown) => {
      onFilterValueChange?.(filterKey, value)
    },
    [onFilterValueChange]
  )

  const handleOperatorChange = useCallback(
    (filterKey: string, operator: FilterOperator) => {
      onFilterOperatorChange?.(filterKey, operator)
    },
    [onFilterOperatorChange]
  )

  const handleRemoveFilter = useCallback(
    (filterKey: string) => {
      onFilterRemove?.(filterKey)
    },
    [onFilterRemove]
  )

  // Get clear button label
  const clearLabel = useMemo(() => {
    if (hasSorts && hasFilters) {
      return 'Clear filters and sorts'
    } else if (hasFilters) {
      return 'Clear filters'
    } else if (hasSorts) {
      return 'Clear sorts'
    }
    return 'Clear'
  }, [hasSorts, hasFilters])

  // Don't render if no active filters, no sorts, AND no unsaved changes
  // Keep bar visible when hasUnsavedChanges so user can click "Save" after clearing
  if (!activeFilters.length && !sorting.length && !hasUnsavedChanges) {
    return null
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-md bg-primary/5 p-2 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex flex-1 flex-wrap items-center gap-2 overflow-x-auto">
        {/* Sort combobox (when sorts exist) */}
        {hasSorts && (
          <>
            <SortCombobox
              sorting={sorting}
              filtersList={sortsList}
              newlyAddedSort={newlyAddedSort}
              onSortSelect={onSortSelect}
              onSortUpdate={onSortUpdate}
              onSortReorder={onSortReorder}
              onClearNewlyAddedSort={onClearNewlyAddedSort}
              clearSorts={clearSorts}
              className="text-xs bg-background/50"
            />
            {hasFilters && (
              <Separator
                orientation="vertical"
                className="h-6 w-px bg-border/50"
              />
            )}
          </>
        )}

        {/* Active filter chips with value selection */}
        {activeFilters.map((filter) => {
          const value = filterValues[filter.filterKey]
          const hasOptions = Boolean(filter.listContent?.length)
          const editorType = getFilterEditorType(filter.dataType, hasOptions)
          const operator =
            filterOperators[filter.filterKey] ?? getDefaultOperator(editorType)
          const isNewlyAdded = newlyAddedFilter === filter.filterKey
          return (
            <FilterChip
              key={filter.filterKey}
              filter={filter}
              value={value}
              operator={operator}
              onValueChange={handleValueChange}
              onOperatorChange={handleOperatorChange}
              onRemove={handleRemoveFilter}
              isNewlyAdded={isNewlyAdded}
              onClearNewlyAdded={onClearNewlyAddedFilter}
            />
          )
        })}

        {/* Add filter button */}
        <FiltersCombobox
          triggerTitle="Add Filter"
          filtersList={filtersList}
          selectedFilters={activeFilters.map((f) => f.filterKey)}
          onFilterSelect={onFilterSelect}
          contentAlign="start"
          triggerIcon={<></>}
          className="h-8 text-xs"
        />

        {/* Result count */}
        {totalMatches !== undefined && totalMatches > 0 && (
          <>
            {isLoading ? (
              <Skeleton className="h-4 w-20 rounded-sm" />
            ) : (
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {totalMatches} results
              </span>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Clear / Revert menu */}
        {(hasFilters || hasSorts || hasUnsavedChanges) && onRevertToSaved && (
          <ResetFiltersMenu
            label={clearLabel}
            hasCurrentFilters={!!(hasFilters || hasSorts)}
            hasUnsavedChanges={hasUnsavedChanges}
            onResetAll={handleClearAll}
            onRevertToSaved={onRevertToSaved}
          />
        )}

        {/* Save / Update View dropdown */}
        {hasUnsavedChanges && onSaveView && onSaveAsNewView && (
          <SaveViewDropdown
            onUpdateView={onSaveView}
            onSaveAsNewView={onSaveAsNewView}
            canUpdateView={canUpdateView}
            isSaving={isSaving}
          />
        )}
      </div>
    </div>
  )
}

const MemoizedFiltersMenu = memo(FiltersMenu)
MemoizedFiltersMenu.displayName = 'FiltersMenu'

export { MemoizedFiltersMenu as FiltersMenu }
